import type { ReactNode } from "react";
import { motion } from "framer-motion";
import type { Categorization } from "../types/transaction";
import { accountLabel } from "../utils/accounts";
import { formatAmount } from "../utils/currency";
import { formatShortDate } from "../utils/dates";
import { RedoButton } from "./review/RedoButton";
import { Tooltip } from "./Tooltip";

interface ReviewedTransactionRowProps {
  item: Categorization;
  /** The list response's ledger_readable: false hides every date label. */
  ledgerReadable: boolean;
  /** True while this row's undo is in flight or its refreshed item is due. */
  isUndoDisabled: boolean;
  onUndo: (itemId: number) => void;
  /** Redo the change the last undo took back; offered only when `can_redo`. */
  onRedo?: (itemId: number) => void;
  /** True while this row's redo is in flight or its refreshed item is due. */
  isRedoDisabled?: boolean;
  /** The server's label for an account it names (the pick list's `labels`). */
  labels?: Record<string, string>;
}

// D1 amendment 2.8 (2): true whoever made the change, client, support or system.
const UNDO_LABEL = "Undo the last change to this entry";
// D1 amendment 2.8 (6): every label line ends with the support address.
const SUPPORT_SUFFIX = "· support@eledger.com";

/**
 * The reviewed row's one merged label line (D1 amendment 2.8 (6)).
 *
 * "In your books since <date>" when a live entry exists, "Reviewed on <date>"
 * otherwise, both dated from `confirmed_at` (when the current booking took
 * effect) and ending in the support address. Only the support address when
 * the books could not be read, when a system booking is not in the books
 * (nobody reviewed it), or when no booking date was served.
 */
function bookedLabel(item: Categorization, ledgerReadable: boolean): string {
  if (!ledgerReadable) return SUPPORT_SUFFIX;
  if (!item.in_books && item.booked_by === "system") return SUPPORT_SUFFIX;
  // confirmed_at is an ISO 8601 timestamp; the screen shows its date part in
  // the same short form as the transaction date.
  const date = formatShortDate(item.confirmed_at?.slice(0, 10) ?? null);
  if (!date) return SUPPORT_SUFFIX;
  const lead = item.in_books ? "In your books since" : "Reviewed on";
  return `${lead} ${date} ${SUPPORT_SUFFIX}`;
}

export function ReviewedTransactionRow({
  item,
  ledgerReadable,
  isUndoDisabled,
  onUndo,
  onRedo,
  isRedoDisabled = false,
  labels = {},
}: ReviewedTransactionRowProps): ReactNode {
  return (
    <motion.div
      className={`reviewed-row status-${item.status}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      layout
    >
      <div className="reviewed-row-left">
        <span className="reviewed-date">{formatShortDate(item.date)}</span>
        <span className="reviewed-desc">{item.description}</span>
      </div>
      <div className="reviewed-row-right">
        <span className="reviewed-account">{accountLabel(item.account_name, labels)}</span>
        <span className="reviewed-amount">{formatAmount(item.amount)}</span>
        <span className="reviewed-date">{bookedLabel(item, ledgerReadable)}</span>
        {item.booked_by === "system" && <span className="reviewed-date">Booked automatically</span>}
        {item.can_undo && (
          <Tooltip text={UNDO_LABEL}>
            <button
              type="button"
              className="reviewed-undo"
              onClick={() => onUndo(item.id)}
              disabled={isUndoDisabled}
              aria-label={UNDO_LABEL}
            >
              &#8634;
            </button>
          </Tooltip>
        )}
        {item.can_redo && onRedo && (
          <RedoButton disabled={isRedoDisabled} onRedo={() => onRedo(item.id)} />
        )}
      </div>
    </motion.div>
  );
}
